import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { useMemo } from 'react';
import { STAGE_ORDER, STAGE_IDS } from '../../NewKB/utils/NewKBConstants';

/**
 * Custom hook to create a TanStack Table instance for Kanban board
 * @param {Array} leads - Array of leads from the server
 * @param {Array} stages - Array of stage objects
 * @returns {Object} - TanStack Table instance
 */
// Default stage data to use when API data isn't available yet
const DEFAULT_STAGES = [
  { _id: STAGE_IDS.LEAD_IN, name: 'Lead In', color: '#4299E1' },
  { _id: STAGE_IDS.NURTURE, name: 'Nurture', color: '#F6AD55' },
  { _id: STAGE_IDS.PROPOSAL_NEEDED, name: 'Proposal Needed', color: '#68D391' },
  { _id: STAGE_IDS.FIX_MEETING, name: 'Fix Meeting', color: '#9F7AEA' },
  { _id: STAGE_IDS.CLOSED_WON, name: 'Closed Won', color: '#48BB78' },
  { _id: STAGE_IDS.CLOSED_LOST, name: 'Closed Lost', color: '#F56565' },
];

export const useKanbanTable = (leads = [], stages = []) => {
  // Ensure leads is always an array
  const safeLeads = Array.isArray(leads) ? leads : [];
  
  // Organize leads by stage
  const kanbanData = useMemo(() => {
    // Use default stages if API stages aren't available or empty
    const stagesData = Array.isArray(stages) && stages.length > 0 ? stages : DEFAULT_STAGES;
    console.log('useKanbanTable - stagesData:', stagesData);
    
    // Log the first few leads to see their stage values
    if (safeLeads.length > 0) {
      console.log('First 3 leads stage values:', safeLeads.slice(0, 3).map(lead => {
        return {
          leadId: lead.leadId || lead._id,
          stage: lead.stage,
          stageType: typeof lead.stage
        };
      }));
    }
    
    // Always use hardcoded stages to ensure consistency
    // This prevents issues with different stage ID formats from API
    console.log('Using DEFAULT_STAGES for consistency');
    const orderedStages = [...DEFAULT_STAGES].sort((a, b) => {
      const indexA = STAGE_ORDER.indexOf(a._id);
      const indexB = STAGE_ORDER.indexOf(b._id);
      return indexA - indexB;
    });
    console.log('useKanbanTable - orderedStages:', orderedStages);
    
    // Create a row for each stage with its leads
    return orderedStages.map(stage => {
      // Enhanced stage matching with normalized stage IDs
      const stageLeads = safeLeads.filter(lead => {
        // Handle leads with no stage - put them in Lead In by default
        if (!lead.stage) {
          return stage._id === STAGE_IDS.LEAD_IN;
        }
        
        // Get the stage ID from the lead regardless of format
        let leadStageId;
        
        if (typeof lead.stage === 'object' && lead.stage !== null) {
          // Handle object format: { _id: 'stage_id' }
          leadStageId = lead.stage._id || '';
        } else if (typeof lead.stage === 'string') {
          // Handle string format
          leadStageId = lead.stage;
        } else {
          // Default to Lead In for any other format
          return stage._id === STAGE_IDS.LEAD_IN;
        }
        
        // Normalize stage IDs by removing 'stage_' prefix if present
        const normalizedLeadStageId = leadStageId.replace(/^stage_/, '');
        const normalizedStageId = stage._id.replace(/^stage_/, '');
        
        // Check for matches with multiple formats
        const isMatch = (
          // Direct match
          leadStageId === stage._id || 
          // Normalized match
          normalizedLeadStageId === normalizedStageId ||
          // Check other possible formats
          `stage_${normalizedLeadStageId}` === stage._id ||
          leadStageId === `stage_${normalizedStageId}`
        );
        
        // Log matches for debugging
        if (isMatch && (Math.random() < 0.1)) { // Log only 10% of matches to avoid spam
          console.log(`Match found: lead ${lead.leadId || lead._id} in stage ${stage.name}`);
        }
        
        return isMatch;
      });
      
      // Log for debugging with ratio of total leads
      const ratio = safeLeads.length > 0 ? (stageLeads.length / safeLeads.length * 100).toFixed(1) : 0;
      console.log(`Stage ${stage.name} (${stage._id}): found ${stageLeads.length} leads (${ratio}% of total)`);
      
      // Verify leads aren't assigned to multiple stages (debug check)
      if (stageLeads.length > 0) {
        const sampleLead = stageLeads[0];
        const leadId = sampleLead.leadId || sampleLead._id;
        const originalStage = typeof sampleLead.stage === 'object' ? 
          JSON.stringify(sampleLead.stage) : sampleLead.stage;
        console.log(`Sample lead in ${stage.name}: ID=${leadId}, original stage=${originalStage}`);
      }
      
      return {
        id: stage._id,
        stage,
        leads: stageLeads,
      };
    });
  }, [safeLeads, stages]);
  
  // Define table columns for Kanban
  const columns = useMemo(() => [
    {
      id: 'stage',
      header: ({ table }) => <span>Stage</span>,
      cell: info => info.row.original.stage,
    },
    {
      id: 'leads',
      header: 'Leads',
      cell: info => info.row.original.leads,
    },
  ], []);
  
  // Get the stages to use (either from API or defaults)
  const stagesData = Array.isArray(stages) && stages.length > 0 ? stages : DEFAULT_STAGES;

  // Create and return TanStack Table instance
  const table = useReactTable({
    data: kanbanData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      // Additional meta information for the table
      rowCount: safeLeads.length,
      stageOrder: STAGE_ORDER,
    },
  });
  
  return { table, stages: stagesData };
};

export default useKanbanTable;
